import React, { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Search, Download, Upload, Plus, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, downloadBlob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SummaryCards from "@/components/training/SummaryCards";
import AttentionPanel from "@/components/training/AttentionPanel";
import OverviewTab from "@/components/training/OverviewTab";
import RecordsTab from "@/components/training/RecordsTab";
import MatrixTab from "@/components/training/MatrixTab";
import CoursesTab from "@/components/training/CoursesTab";
import StaffProfileDrawer from "@/components/training/StaffProfileDrawer";
import TrainingRecordDialog from "@/components/training/TrainingRecordDialog";
import BulkImportDialog from "@/components/BulkImportDialog";
import MobileTrainingHome from "@/components/mobile/MobileTrainingHome";

const TABS = [
  { key: "overview",  label: "Overview" },
  { key: "records",   label: "Training Records" },
  { key: "matrix",    label: "Training Matrix" },
  { key: "courses",   label: "Courses" },
];

export default function Training() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "overview");
  const [query, setQuery] = useState("");
  const [summary, setSummary] = useState(null);
  const [actions, setActions] = useState({ expired: [], expiring: [], missing: [], total: 0 });
  const [refreshTick, setRefreshTick] = useState(0);
  const [staffDrawerId, setStaffDrawerId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);

  const canManage = ["admin", "manager", "registered_manager", "deputy_manager", "service_manager", "super_admin"].includes(user?.role);

  useEffect(() => { setSp((s) => { s.set("tab", tab); return s; }, { replace: true }); }, [tab, setSp]);

  const load = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([
        api.get("/training/summary"),
        api.get("/training/actions"),
      ]);
      setSummary(s.data);
      setActions(a.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load, refreshTick]);

  const refresh = () => { setRefreshTick((x) => x + 1); toast.success("Refreshed"); };

  const exportPdf = async () => {
    try { await downloadBlob(`/reports/module/training`, `ayobell-training.pdf`); toast.success("PDF downloaded"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* ================ MOBILE (<lg) ================ */}
      <div className="lg:hidden flex-1 min-h-0 flex flex-col" data-testid="training-mobile-wrap">
        <MobileTrainingHome
          tab={tab}
          setTab={setTab}
          summary={summary}
          actions={actions}
          canManage={canManage}
          refreshTick={refreshTick}
          onRefresh={refresh}
          onNewRecord={() => setNewOpen(true)}
          onImportCsv={() => setCsvOpen(true)}
          onExportPdf={exportPdf}
          onOpenStaff={setStaffDrawerId}
          onChanged={() => setRefreshTick((x) => x + 1)}
        />
      </div>

      {/* ================ DESKTOP (>=lg) — untouched ================ */}
      <div className="hidden lg:flex flex-1 min-h-0 flex-col" data-testid="training-desktop-wrap">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-6 py-4 shrink-0">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-center">
              <GraduationCap className="w-4 h-4 text-sky-700" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Governance</div>
              <h1 className="font-heading text-xl font-semibold text-slate-900" data-testid="training-page-title">Training &amp; Compliance</h1>
              <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">Monitor staff training, mandatory requirements, certificates and expiry dates across your organisation.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search staff or course…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="training-search"
                className="w-64 h-9 pl-8 text-sm bg-white"
              />
            </div>
            <Button variant="outline" size="sm" onClick={refresh} data-testid="btn-training-refresh" className="h-9 text-slate-700">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
            {canManage && (
              <Button variant="outline" size="sm" onClick={() => setCsvOpen(true)} data-testid="btn-training-import-csv" className="h-9 text-slate-700">
                <Upload className="w-3.5 h-3.5 mr-1.5" /> Import CSV
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={exportPdf} data-testid="btn-training-export-pdf" className="h-9 text-slate-700">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export PDF
            </Button>
            {canManage && (
              <Button size="sm" onClick={() => setNewOpen(true)} data-testid="btn-training-new-record" className="h-9 bg-sky-700 hover:bg-sky-800">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> New Record
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-4 flex gap-0 border-b border-slate-200 -mb-4">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`training-tab-${t.key}`}
                className={`px-4 py-2 text-xs font-medium border-b-2 transition ${active ? "border-sky-700 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Summary strip (visible on all tabs) */}
      <div className="px-6 pt-4 shrink-0 bg-slate-50 border-b border-slate-200 pb-4 space-y-3">
        <SummaryCards summary={summary} />
        <AttentionPanel actions={actions} onView={() => setTab("records")} />
      </div>

      {/* Tab body */}
      <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50">
        <div className="p-6">
          {tab === "overview" && <OverviewTab summary={summary} actions={actions} onOpenStaff={setStaffDrawerId} refreshTick={refreshTick} />}
          {tab === "records" && (
            <RecordsTab
              search={query}
              onOpenStaff={setStaffDrawerId}
              onChanged={() => setRefreshTick((x) => x + 1)}
              canManage={canManage}
              refreshTick={refreshTick}
            />
          )}
          {tab === "matrix" && <MatrixTab onOpenStaff={setStaffDrawerId} refreshTick={refreshTick} />}
          {tab === "courses" && <CoursesTab canManage={canManage} onChanged={() => setRefreshTick((x) => x + 1)} refreshTick={refreshTick} />}
        </div>
      </div>
      </div>{/* /desktop-wrap */}

      {/* Drawers / dialogs */}
      <StaffProfileDrawer
        staffId={staffDrawerId}
        onClose={() => setStaffDrawerId(null)}
        canManage={canManage}
        onChanged={() => setRefreshTick((x) => x + 1)}
      />
      <TrainingRecordDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onSaved={() => { setNewOpen(false); setRefreshTick((x) => x + 1); }}
      />
      <BulkImportDialog
        open={csvOpen}
        onOpenChange={setCsvOpen}
        module={{ key: "training", label: "Training", fields: [
          { name: "staff_name", label: "Staff Name" },
          { name: "course", label: "Course" },
          { name: "provider", label: "Provider" },
          { name: "completed_date", label: "Completed" },
          { name: "expiry_date", label: "Expiry" },
          { name: "certificate_id", label: "Certificate" },
          { name: "notes", label: "Notes" },
        ]}}
        onDone={() => setRefreshTick((x) => x + 1)}
      />
    </div>
  );
}
